// @ts-nocheck
/**
 * Ludipek mobile navigation drawer.
 *
 * Opens the <dialog> rendered by snippets/ludipek-nav-drawer.liquid from the header's
 * hamburger button. The browser gives us Escape, the focus trap and the backdrop; this
 * script adds the three things it does not:
 *
 *   1. Horizon's `scroll-lock` attribute, so the page behind the drawer freezes exactly
 *      like it does behind the search and cart dialogs (assets/dialog.js sets the same
 *      attribute; base.css is what actually freezes <html>).
 *   2. A backdrop click-to-close — ::backdrop clicks land on the <dialog> element itself,
 *      so "outside" means "not inside the panel".
 *   3. A close on navigation. The drawer is full of links, and a browser that restores the
 *      page from bfcache would otherwise bring the user back to an open drawer.
 *   4. A close on same-page hash links ("Ver todas las marcas" tapped on /collections
 *      itself). Those scroll instead of navigating, so the modal would stay open over the
 *      section it just scrolled to, with the page scroll-locked behind it.
 *
 * The slide-in is a CSS transition on the panel keyed off the [open] attribute. Closing has
 * to wait for it, so `close()` is deferred behind a transitionend — with a timeout fallback,
 * because a drawer that never closes is worse than one that closes without animating.
 */
(function () {
  const SCROLL_LOCK_ATTRIBUTE = 'scroll-lock';
  const CLOSE_FALLBACK_MS = 400;

  const dialog = document.querySelector('[data-lp-nav-drawer]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const panel = dialog.querySelector('.lp-navdrawer__panel');

  function open() {
    dialog.showModal();
    // Set after showModal so the panel starts at its closed transform and animates in.
    requestAnimationFrame(() => dialog.setAttribute('data-open', ''));
    document.documentElement.setAttribute(SCROLL_LOCK_ATTRIBUTE, '');
  }

  /**
   * Close the drawer after its slide-out.
   * @param {() => void} [afterClose] Runs once the dialog is closed and the scroll lock released.
   */
  function close(afterClose) {
    if (!dialog.open) return;
    dialog.removeAttribute('data-open');

    let closed = false;
    /** Actually dismiss the dialog and release the scroll lock, at most once. */
    function finish() {
      if (closed) return;
      closed = true;
      dialog.close();
      document.documentElement.removeAttribute(SCROLL_LOCK_ATTRIBUTE);
      if (typeof afterClose === 'function') afterClose();
    }

    if (panel === null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }
    panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, CLOSE_FALLBACK_MS);
  }

  document.querySelectorAll('[data-lp-nav-open]').forEach((trigger) => {
    trigger.addEventListener('click', open);
  });
  dialog.querySelectorAll('[data-lp-nav-close]').forEach((trigger) => {
    trigger.addEventListener('click', () => close());
  });

  // Same-page hash link: close first, then scroll, so the target is not hidden behind the
  // modal and the scroll lock is gone by the time the page moves.
  dialog.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    const target = new URL(link.href, window.location.href);
    const samePage =
      target.origin === window.location.origin &&
      target.pathname === window.location.pathname &&
      target.search === window.location.search;
    if (!samePage || target.hash === '') return;
    event.preventDefault();
    close(() => {
      if (window.location.hash !== target.hash) {
        window.location.hash = target.hash;
        return;
      }
      // Same hash already in the URL: assigning it again would not scroll.
      const section = document.getElementById(decodeURIComponent(target.hash.slice(1)));
      if (section) section.scrollIntoView();
    });
  });

  // Escape fires the dialog's own `cancel`; intercept it so the close animation runs and the
  // scroll lock is released, instead of the browser hard-closing behind our back.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener('click', (event) => {
    if (panel !== null && !panel.contains(event.target)) close();
  });

  // Restored from bfcache with the drawer still open — dismiss it without animating.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && dialog.open) {
      dialog.removeAttribute('data-open');
      dialog.close();
      document.documentElement.removeAttribute(SCROLL_LOCK_ATTRIBUTE);
    }
  });
})();
